import {
  type TipoEvento,
  avisoAcimaDaFicha,
  situacaoNoFimDoTurno,
  eventosDoBanco,
  formatarCronometro,
  progressoTempo,
  reduzirEventos,
} from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useSubscription } from '@trpc/tanstack-react-query'
import {
  ArrowLeft,
  Brush,
  Check,
  ChevronRight,
  ClipboardCheck,
  CloudOff,
  FileCheck,
  Hash,
  Inbox,
  ListChecks,
  LogOut,
  Pause,
  Play,
  Repeat,
  Square,
  UserPlus,
  X,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IndicadorOffline } from '@/components/indicador-offline'
import { LinkSuporte } from '@/components/link-suporte'
import { MarcaOP } from '@/components/marca'
import { AreaCentralizada } from '@/components/posto/area-centralizada'
import {
  AtividadeEmAndamento,
  EscolherAtividade,
  type OpcaoAtividade,
} from '@/components/posto/atividade-sem-ficha'
import { AvisoFimDoTurno } from '@/components/posto/aviso-fim-do-turno'
import { ConfirmarConclusao, type FaltaInformada } from '@/components/posto/confirmar-conclusao'
import { Cronometro } from '@/components/posto/cronometro'
import { MeuFechamento } from '@/components/posto/meu-fechamento'
import { ConviteInstalar } from '@/components/pwa/convite-instalar'
import { PinPad } from '@/components/posto/pin-pad'
import { ResumoDoDia } from '@/components/posto/resumo-do-dia'
import { SemConexao } from '@/components/posto/sem-conexao'
import { TecladoNumerico } from '@/components/posto/teclado-numerico'
import { VincularTablet } from '@/components/posto/vincular-tablet'
import { Button } from '@/components/ui/button'
import { EsqueletoCartoes, EsqueletoLinhas, EstadoVazio } from '@/components/ui/estado'
import { SemaforoBadge } from '@/components/ui/semaforo-badge'
import { toast } from '@/components/ui/toast'
import { useRelogio } from '@/hooks/use-relogio'
import { useTelaLigada } from '@/hooks/use-tela-ligada'
import { cn } from '@/lib/cn'
import { formatarData } from '@/lib/datas'
import { erroDeDispositivo } from '@/lib/dispositivo'
import { mensagemDeErro } from '@/lib/erros'
import { iniciais, primeiroNome } from '@/lib/nomes'
import { plural } from '@/lib/formato'
import { type EntradaOutbox, Outbox, OutboxDb, ehAtividade } from '@/lib/offline/outbox'
import { projetarAtividade, projetarFila } from '@/lib/offline/projecao'
import { lembrarArea } from '@/lib/pwa'
import { prazoDeHoje, prazoVencido } from '@/lib/pedidos'
import { useTRPC, useTRPCClient } from '@/lib/trpc'

export const Route = createFileRoute('/posto/$postoId')({ component: TelaPosto })

/** Prazo é coluna DATE (meia-noite UTC): lê em UTC para não voltar um dia no Brasil. */
const diaMes = (d: Date) =>
  d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })

const hora = (d: Date | string) =>
  new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

/**
 * Tela do tablet do posto. Casca do tamanho exato da tela (`h-dvh`): o
 * cabeçalho é fixo e só listas internas rolam — a página nunca rola, deitado
 * ou em pé.
 */
function TelaPosto() {
  const { postoId } = Route.useParams()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const posto = useQuery(trpc.postos.porId.queryOptions({ id: postoId }))
  // Identidade do tablet (ADR 0010): sem vínculo com este posto, nada abre.
  // Revisita a cada minuto para um tablet revogado cair na tela de vínculo.
  const dispositivo = useQuery({
    ...trpc.dispositivos.meu.queryOptions(),
    refetchInterval: 60_000,
    // Sem rede, "Sem conexão" tem de aparecer em segundos, não depois de 3 tentativas.
    retry: 1,
  })
  const me = useQuery({ ...trpc.auth.me.queryOptions(), retry: false })
  const [sobreposicao, setSobreposicao] = useState<'resumo' | 'fechamento' | null>(null)
  // "Sair" passa pela fila: ficha em execução não pode ficar correndo sozinha.
  const [pedidoSaida, setPedidoSaida] = useState(false)
  // Tablet do posto: tela sempre acesa e o app instalado abre direto aqui.
  useTelaLigada()
  useEffect(() => lembrarArea('/posto'), [])

  // Botões do operador só com o tablet deste posto: sem vínculo, a tela é a de vincular.
  const logadoNestePosto = me.data?.postoId === postoId && dispositivo.data?.posto.id === postoId
  const mensagemSuporte = `Olá! Preciso de ajuda no posto ${posto.data?.nome ?? postoId}${
    me.data?.nome ? ` (${me.data.nome})` : ''
  }.`
  const nomeSetor = posto.data?.setor.nome
  const nomePosto = posto.data?.nome
  const subtitulo = [
    nomeSetor && nomeSetor !== nomePosto ? nomeSetor : null,
    logadoNestePosto ? me.data?.nome : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <main className="flex h-dvh flex-col overflow-hidden select-none [-webkit-touch-callout:none]">
      {/* Altura fixa e uma linha só: título trunca, botões nunca quebram. */}
      <header className="flex h-18 shrink-0 items-center justify-between gap-3 border-b border-border px-4 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <MarcaOP className="size-9 shrink-0" />
          <div className="min-w-0">
            <h1 className="truncate text-2xl leading-tight font-bold">
              {nomePosto ?? (posto.isError ? 'Posto' : ' ')}
            </h1>
            {subtitulo && <p className="truncate text-sm text-fg-muted">{subtitulo}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 md:gap-2">
          <LinkSuporte
            whatsapp={posto.data?.suporte.whatsapp}
            mensagem={mensagemSuporte}
            tamanho="tap"
            compacto
          />
          {logadoNestePosto && (
            <>
              <Button
                variant="ghost"
                size="tap"
                className="px-4 whitespace-nowrap max-sm:px-3"
                onClick={() => setSobreposicao('resumo')}
              >
                <ClipboardCheck className="size-6" aria-hidden />
                <span className="max-sm:sr-only">Meu dia</span>
              </Button>
              <Button
                variant="ghost"
                size="tap"
                className="px-4 whitespace-nowrap max-sm:px-3"
                onClick={() => setSobreposicao('fechamento')}
                aria-label="Meu fechamento"
              >
                <FileCheck className="size-6" aria-hidden />
                <span className="hidden xl:inline">Meu fechamento</span>
                <span className="max-sm:sr-only xl:hidden">Fechamento</span>
              </Button>
              <BotaoSair onClick={() => setPedidoSaida(true)} pendente={pedidoSaida} />
            </>
          )}
        </div>
      </header>
      {posto.data === null ? (
        <AreaCentralizada>
          <EstadoVazio
            tamanho="lg"
            titulo="Posto não encontrado"
            texto="Confira o endereço com a gestão."
          />
        </AreaCentralizada>
      ) : dispositivo.isError && !dispositivo.data ? (
        <SemConexao
          onTentarDeNovo={() => void dispositivo.refetch()}
          tentando={dispositivo.isFetching}
        />
      ) : dispositivo.data === undefined ? (
        <AreaCentralizada>
          <EsqueletoLinhas linhas={3} className="w-full max-w-md" rotulo="Identificando o tablet" />
        </AreaCentralizada>
      ) : dispositivo.data === null ? (
        <VincularTablet
          postoSugerido={posto.data ?? null}
          gestao={me.data && me.data.perfil !== 'OPERADOR' ? { nome: me.data.nome } : null}
          onVinculado={(p) =>
            void navigate({ to: '/posto/$postoId', params: { postoId: p.id }, replace: true })
          }
        />
      ) : dispositivo.data.posto.id !== postoId ? (
        <AreaCentralizada className="text-center">
          <div className="flex max-w-md flex-col gap-4">
            <h2 className="text-2xl font-bold text-balance">
              Este tablet é do posto {dispositivo.data.posto.nome}
            </h2>
            <p className="text-lg text-fg-muted">
              Para usá-lo aqui, a gestão precisa revogá-lo em Fábrica → Tablets e gerar um código
              novo para este posto.
            </p>
            <Link
              to="/posto/$postoId"
              params={{ postoId: dispositivo.data.posto.id }}
              className="flex h-tap press items-center justify-center card px-6 text-xl font-semibold hover:border-brand/60"
            >
              Abrir {dispositivo.data.posto.nome}
            </Link>
          </div>
        </AreaCentralizada>
      ) : logadoNestePosto ? (
        <Fila
          postoId={postoId}
          operadorId={me.data?.id ?? ''}
          nome={me.data?.nome ?? ''}
          ajudante={me.data?.ajudante ?? null}
          sobreposicao={sobreposicao}
          onFecharSobreposicao={() => setSobreposicao(null)}
          pedidoSaida={pedidoSaida}
          onSaidaEncerrada={() => setPedidoSaida(false)}
        />
      ) : (
        <LoginPosto postoId={postoId} />
      )}
    </main>
  )
}

/** Logout do posto: nada do operador que saiu fica em cache para o próximo. */
function useSair() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: async () => {
        // Fila, feitas e resumo são do operador que saiu.
        queryClient.removeQueries({ queryKey: trpc.apontamento.pathKey() })
        queryClient.removeQueries({ queryKey: trpc.avaliacao.pathKey() })
        // `reset`, não `invalidate`: com 401 o React Query manteria o operador antigo em tela.
        await queryClient.resetQueries({ queryKey: trpc.auth.me.queryKey() })
      },
    }),
  )
}

/** Botão do cabeçalho: pede a saída; quem resolve a ficha aberta e sai é a fila. */
function BotaoSair({ onClick, pendente }: { onClick: () => void; pendente: boolean }) {
  return (
    <Button
      variant="ghost"
      size="tap"
      className="px-4 whitespace-nowrap max-sm:px-3"
      onClick={onClick}
      carregando={pendente}
    >
      <LogOut className="size-6" aria-hidden />
      <span className="max-sm:sr-only">Sair</span>
    </Button>
  )
}

interface OperadorDaLista {
  id: string
  nome: string
  recente?: boolean
}

function Avatar({ nome, className }: { nome: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-brand/15 font-bold text-brand',
        className,
      )}
    >
      {iniciais(nome)}
    </span>
  )
}

/**
 * Nomes → PIN, ocupando a área toda. A lista rola por dentro com o título
 * fixo; o PIN fica com quem digita à esquerda e o teclado à direita (deitado).
 * Reutilizada no login e na entrada do ajudante.
 */
function EscolherOperador({
  operadores,
  titulo,
  onPin,
  erro,
  pendente,
  carregando = false,
}: {
  operadores: OperadorDaLista[]
  titulo?: string
  onPin: (operadorId: string, pin: string) => void
  erro: string | null
  pendente: boolean
  carregando?: boolean
}) {
  const [operadorId, setOperadorId] = useState<string | null>(null)
  const operador = operadores.find((o) => o.id === operadorId)
  const recentes = operadores.filter((o) => o.recente)
  const outros = operadores.filter((o) => !o.recente)

  const grade = (lista: OperadorDaLista[]) => (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,19rem))] justify-center gap-3">
      {lista.map((o) => (
        <Button
          key={o.id}
          size="tap"
          variant="secondary"
          className="h-22 justify-start gap-4 px-4 text-xl whitespace-normal"
          onClick={() => setOperadorId(o.id)}
        >
          <Avatar nome={o.nome} className="size-14 text-lg" />
          <span className="line-clamp-2 min-w-0 text-left leading-tight">{o.nome}</span>
        </Button>
      ))}
    </div>
  )

  return (
    <AnimatePresence mode="wait" initial={false}>
      {!operador ? (
        <motion.div
          key="lista"
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.18 }}
          className="min-h-0 w-full flex-1 overflow-y-auto overscroll-contain"
        >
          <div className="flex min-h-full flex-col justify-center gap-6 py-2">
            {titulo && <h2 className="text-center text-3xl font-bold">{titulo}</h2>}
            {carregando ? (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,19rem))] justify-center gap-3">
                <EsqueletoCartoes quantidade={3} />
              </div>
            ) : operadores.length === 0 ? (
              <EstadoVazio
                tamanho="lg"
                titulo="Nenhum operador com PIN"
                texto="A gestão cadastra os operadores e o PIN de cada um em Usuários."
              />
            ) : recentes.length > 0 && outros.length > 0 ? (
              <>
                <section aria-label="Neste posto" className="flex flex-col gap-3">
                  <h3 className="text-center text-sm font-semibold tracking-widest text-fg-muted uppercase">
                    Neste posto
                  </h3>
                  {grade(recentes)}
                </section>
                <section aria-label="Todos" className="flex flex-col gap-3">
                  <h3 className="text-center text-sm font-semibold tracking-widest text-fg-muted uppercase">
                    Todos
                  </h3>
                  {grade(outros)}
                </section>
              </>
            ) : (
              grade(operadores)
            )}
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="pin"
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 16 }}
          transition={{ duration: 0.18 }}
          className="flex min-h-0 w-full flex-1 flex-col justify-center overflow-y-auto"
        >
          <PinPad
            onCompleto={(pin) => onPin(operador.id, pin)}
            erro={erro}
            desabilitado={pendente}
            identidade={
              <>
                <Avatar nome={operador.nome} className="size-20 text-2xl" />
                <h2 className="text-3xl font-bold text-balance">
                  Olá, {primeiroNome(operador.nome)}
                </h2>
              </>
            }
            acoes={
              <Button variant="secondary" size="tap" onClick={() => setOperadorId(null)}>
                <ArrowLeft className="size-6" aria-hidden /> Não sou eu
              </Button>
            }
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function LoginPosto({ postoId }: { postoId: string }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const operadores = useQuery(trpc.auth.operadoresDoPosto.queryOptions({ postoId }))
  const [erro, setErro] = useState<string | null>(null)

  const login = useMutation(
    trpc.auth.loginPosto.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() }),
      onError: (e) => {
        setErro(mensagemDeErro(e))
        // Tablet revogado entre uma tela e outra: volta para "Vincular este tablet".
        if (erroDeDispositivo(e)) {
          void queryClient.invalidateQueries({ queryKey: trpc.dispositivos.meu.queryKey() })
        }
      },
    }),
  )

  if (operadores.isError) {
    return (
      <SemConexao
        onTentarDeNovo={() => void operadores.refetch()}
        tentando={operadores.isFetching}
      />
    )
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3 p-4 md:p-6">
      <ConviteInstalar contexto="posto" className="mx-auto w-full max-w-xl shrink-0" />
      <EscolherOperador
        operadores={operadores.data ?? []}
        carregando={operadores.isPending}
        titulo="Quem está no posto?"
        erro={erro}
        pendente={login.isPending}
        onPin={(operadorId, pin) => {
          setErro(null)
          login.mutate({ postoId, operadorId, pin })
        }}
      />
    </section>
  )
}

type FichaDaFila = NonNullable<ReturnType<typeof useFila>['data']>[number]
type FichaFeita = NonNullable<ReturnType<typeof useFeitasHoje>['data']>[number]
interface Ajudante {
  id: string
  nome: string
}
type ModoFicha = 'acoes' | 'motivos' | 'sair' | 'apontar' | 'concluir' | 'trocar' | 'ajudante'

function useFila() {
  const trpc = useTRPC()
  return useQuery({ ...trpc.apontamento.minhaFila.queryOptions(), refetchInterval: 30_000 })
}

function useFeitasHoje() {
  const trpc = useTRPC()
  return useQuery({ ...trpc.apontamento.concluidasHoje.queryOptions(), refetchInterval: 60_000 })
}

interface FilaProps {
  postoId: string
  operadorId: string
  nome: string
  /** Ajudante confirmado na sessão do posto (vem de auth.me; o servidor é a fonte). */
  ajudante: Ajudante | null
  sobreposicao: 'resumo' | 'fechamento' | null
  onFecharSobreposicao: () => void
  /** O operador tocou em "Sair" no cabeçalho. */
  pedidoSaida: boolean
  /** Saiu ou desistiu de sair. */
  onSaidaEncerrada: () => void
}

/**
 * Operador logado. Deitado: área de trabalho à esquerda, fila à direita.
 * Em pé: área de trabalho em cima, fila embaixo. Um passo aberto (motivo,
 * quantidade, ajudante) toma a tela inteira, com o Voltar sempre visível.
 */
function Fila({
  postoId,
  operadorId,
  nome,
  ajudante,
  sobreposicao,
  onFecharSobreposicao,
  pedidoSaida,
  onSaidaEncerrada,
}: FilaProps) {
  const trpc = useTRPC()
  const trpcClient = useTRPCClient()
  const queryClient = useQueryClient()
  const fila = useFila()
  const feitas = useFeitasHoje()
  const motivos = useQuery(trpc.apontamento.motivosPausa.queryOptions())
  const motivosPerda = useQuery(trpc.apontamento.motivosPerda.queryOptions())
  const atividades = useQuery(trpc.apontamento.atividades.queryOptions())
  const atividadeAtual = useQuery(trpc.apontamento.atividadeAtual.queryOptions())
  const [escolhendoAtividade, setEscolhendoAtividade] = useState(false)
  // Toques ainda no outbox: a tela os projeta por cima do cache (funciona sem rede).
  const [pendentes, setPendentes] = useState<EntradaOutbox[]>([])
  const [aba, setAba] = useState<'fila' | 'feitas'>('fila')
  // O passo pertence à ficha: trocou de ficha (concluiu, outra puxou), volta às ações.
  const [passo, setPasso] = useState<{ fichaEtapaId: string; modo: ModoFicha } | null>(null)

  const invalidarSessao = useCallback(
    () => queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() }),
    [queryClient, trpc],
  )
  const removerAjudante = useMutation(
    trpc.apontamento.removerAjudante.mutationOptions({ onSuccess: invalidarSessao }),
  )

  // Outbox único por tela; sincroniza ao voltar a rede e invalida a fila.
  const outbox = useMemo(
    () =>
      // Um por operador: evento guardado sem rede só é enviado na sessão de quem o fez
      // (nunca em nome do próximo que entrar neste tablet).
      new Outbox(new OutboxDb(`projetive-op-outbox-${operadorId}`), (input) =>
        ehAtividade(input)
          ? trpcClient.apontamento.apontarAtividade.mutate(input)
          : trpcClient.apontamento.registrarEvento.mutate(input),
      ),
    [trpcClient, operadorId],
  )

  const atualizarPendentes = useCallback(
    async () => setPendentes((await outbox.listar()).map((p) => p.input)),
    [outbox],
  )

  const sincronizar = useCallback(() => {
    return outbox.sincronizar().then(async (r) => {
      if (r.recusados.length > 0) {
        // O toque não valeu (ex.: ficha já com outro operador): avisa e recarrega a fila.
        toast.erro(`Um apontamento não foi aceito: ${r.recusados[0]!.mensagem}`)
        await queryClient.invalidateQueries({ queryKey: trpc.apontamento.minhaFila.queryKey() })
      }
      if (r.enviados > 0 || r.recusados.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: trpc.apontamento.atividadeAtual.queryKey(),
        })
      }
      if (r.enviados > 0) {
        await queryClient.invalidateQueries({ queryKey: trpc.apontamento.minhaFila.queryKey() })
        await queryClient.invalidateQueries({ queryKey: trpc.apontamento.resumoDoDia.queryKey() })
        await queryClient.invalidateQueries({
          queryKey: trpc.apontamento.concluidasHoje.queryKey(),
        })
        // Concluir a ficha tira o ajudante da sessão no servidor.
        await invalidarSessao()
      }
      // Só depois de o servidor responder: a projeção não "volta" um passo no meio.
      await atualizarPendentes()
      return r
    })
  }, [outbox, queryClient, trpc, invalidarSessao, atualizarPendentes])

  // Reenvio: ao voltar a rede e, por garantia, a cada 10 s.
  useEffect(() => {
    const enviar = () => void sincronizar()
    enviar()
    window.addEventListener('online', enviar)
    const id = setInterval(enviar, 10_000)
    return () => {
      window.removeEventListener('online', enviar)
      clearInterval(id)
    }
  }, [sincronizar])

  const registrar = async (
    fichaEtapaId: string,
    tipo: TipoEvento,
    extra: {
      motivoPausaId?: string
      quantidade?: number
      destinoFalta?: FaltaInformada['destino']
      motivoPerdaId?: string
    } = {},
  ) => {
    await outbox.enfileirar({
      fichaEtapaId,
      tipo,
      idempotencyKey: crypto.randomUUID(),
      emCliente: new Date(),
      ...extra,
      // Ajudante confirmado na sessão assina tudo até a ficha fechar (RF-T07).
      ...(ajudante ? { ajudanteId: ajudante.id } : {}),
    })
    await atualizarPendentes()
    void sincronizar()
  }

  const enfileirar = async (input: Parameters<typeof outbox.enfileirar>[0]) => {
    await outbox.enfileirar(input)
    await atualizarPendentes()
    void sincronizar()
  }

  // Projeção barata (dezenas de eventos): recalcula a cada render.
  const filaVista = fila.data ? projetarFila(fila.data, pendentes) : undefined
  const atividadeAberta = projetarAtividade(
    atividadeAtual.data ?? null,
    pendentes,
    atividades.data ?? [],
  )

  // Atividade sem ficha (K44): a tela muda na hora; o envio vai pela mesma fila offline.
  const iniciarAtividade = (atividade: OpcaoAtividade) => {
    const chave = crypto.randomUUID()
    const agora = new Date()
    setEscolhendoAtividade(false)
    void enfileirar({
      acao: 'iniciar',
      atividadeId: atividade.id,
      idempotencyKey: chave,
      emCliente: agora,
    })
  }
  const encerrarAtividade = () => {
    const atual = atividadeAberta
    if (!atual) return
    void enfileirar({
      acao: 'encerrar',
      chaveInicio: atual.chaveInicio,
      idempotencyKey: crypto.randomUUID(),
      emCliente: new Date(),
    })
    toast.sucesso(`${atual.atividade.nome}: encerrada.`)
  }

  const emAndamento = filaVista?.find((f) => f.estado !== 'AGUARDANDO')
  const emAtividade = !emAndamento ? atividadeAberta : null
  const aguardando = filaVista?.filter((f) => f.estado === 'AGUARDANDO') ?? []

  // Tempo real: quando outro setor conclui ou a PCP lança um pedido, a fila se
  // refaz na hora (a consulta a cada 30 s continua como garantia sem conexão).
  useSubscription(
    trpc.painel.onAtualizacao.subscriptionOptions(undefined, {
      onData: (evento) => {
        if (evento.tipo === 'ficha_etapa_atualizada' || evento.tipo === 'pedido_criado') {
          void queryClient.invalidateQueries({ queryKey: trpc.apontamento.minhaFila.queryKey() })
        }
        // O ajudante voltou ao posto dele: este tablet relê a sessão (sai o "+ João").
        if (evento.tipo === 'ajuda_alterada' && evento.id === postoId) {
          void queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() })
        }
      },
    }),
  )
  // Ficha que chegou agora na fila deste setor: um aviso, para ninguém ficar parado.
  const conhecidas = useRef<Set<string> | null>(null)
  const idsAguardando = aguardando.map((f) => f.id).join(',')
  useEffect(() => {
    if (!fila.data) return
    const atuais = (filaVista ?? []).filter((f) => f.estado === 'AGUARDANDO')
    const anteriores = conhecidas.current
    conhecidas.current = new Set(atuais.map((f) => f.id))
    if (!anteriores) return
    const novas = atuais.filter((f) => !anteriores.has(f.id))
    if (novas.length === 1) {
      toast.info(`Nova ficha na fila: ${novas[0]!.ficha.itemPedido.produto.nome}`)
    } else if (novas.length > 1) {
      toast.info(`${novas.length} fichas novas na fila`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só quando muda o conjunto de fichas
  }, [idsAguardando])
  // Sair: envia o que estiver guardado e encerra a sessão. Com ficha em execução,
  // primeiro pede o motivo da pausa: ela não pode ficar correndo com o posto vazio.
  const sair = useSair()
  const saindo = useRef(false)
  const sairAgora = useCallback(async () => {
    if (saindo.current) return
    saindo.current = true
    await sincronizar().catch(() => undefined)
    sair.mutate(undefined, {
      onSettled: () => {
        saindo.current = false
        onSaidaEncerrada()
      },
    })
  }, [sincronizar, sair, onSaidaEncerrada])
  const pedeMotivoParaSair = pedidoSaida && emAndamento?.estado === 'EM_EXECUCAO'
  useEffect(() => {
    if (pedidoSaida && !pedeMotivoParaSair) void sairAgora()
  }, [pedidoSaida, pedeMotivoParaSair, sairAgora])
  const pausarESair = async (motivoPausaId: string) => {
    if (!emAndamento) return
    await registrar(emAndamento.id, 'pausar', { motivoPausaId })
    await sairAgora()
  }

  // Fim do turno (ADR 0016): o tablet pergunta; o servidor pausa e sai se ninguém responder.
  const fimDoTurno = useQuery({
    ...trpc.apontamento.fimDoTurno.queryOptions(),
    refetchInterval: 60_000,
  })
  const relogioTurno = useRelogio(15_000)
  const confirmarHoraExtra = useMutation(
    trpc.apontamento.confirmarHoraExtra.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: trpc.apontamento.fimDoTurno.queryKey() }),
    }),
  )
  const turno = fimDoTurno.data
  const noFimDoTurno = turno
    ? situacaoNoFimDoTurno({
        fimDoTurno: new Date(turno.fimDoTurno),
        agora: relogioTurno,
        esperaSeg: turno.esperaMin * 60,
        horaExtraEm: turno.horaExtraEm ? new Date(turno.horaExtraEm) : null,
      })
    : null
  const perguntarFimDoTurno =
    noFimDoTurno?.situacao === 'perguntar' || noFimDoTurno?.situacao === 'encerrar'
  const fichaRodando = emAndamento?.estado === 'EM_EXECUCAO'
  const pausarESairNoFimDoTurno = () => {
    if (fichaRodando && turno?.motivoFimId) void pausarESair(turno.motivoFimId)
    else void sairAgora()
  }

  const modo: ModoFicha = pedeMotivoParaSair
    ? 'sair'
    : emAndamento && passo?.fichaEtapaId === emAndamento.id
      ? passo.modo
      : 'acoes'
  const emPasso = modo !== 'acoes'
  const mudarModo = (m: ModoFicha) => {
    // Voltar do "Antes de sair" = desistiu de sair.
    if (modo === 'sair' && m !== 'sair') onSaidaEncerrada()
    setPasso(
      emAndamento && m !== 'acoes' && m !== 'sair'
        ? { fichaEtapaId: emAndamento.id, modo: m }
        : null,
    )
  }

  const concluir = (ficha: FichaDaFila, quantidade: number, falta?: FaltaInformada) => {
    setPasso(null)
    void registrar(ficha.id, 'concluir', {
      quantidade,
      ...(falta ? { destinoFalta: falta.destino } : {}),
      ...(falta?.motivoPerdaId ? { motivoPerdaId: falta.motivoPerdaId } : {}),
    })
    // Aparece já em "Feitas hoje", mesmo antes de o servidor confirmar (ou sem rede).
    queryClient.setQueryData(trpc.apontamento.concluidasHoje.queryKey(), (atual) => [
      {
        id: ficha.id,
        concluidoEm: new Date(),
        quantidade,
        etapa: ficha.etapa.nome,
        produto: ficha.ficha.itemPedido.produto.nome,
        pedido: ficha.ficha.itemPedido.pedido.numero,
        ficha: { numero: ficha.ficha.numero, total: ficha.ficha.total },
      },
      ...(atual ?? []).filter((f) => f.id !== ficha.id),
    ])
    const faltou = Math.max(
      0,
      ficha.ficha.quantidade -
        reduzirEventos(eventosDoBanco(ficha.eventos)).quantidadeApontada -
        quantidade,
    )
    toast.sucesso(
      falta?.destino === 'saldo'
        ? `Ficha concluída: ${plural(quantidade, 'peça', 'peças')}. As ${faltou} que faltaram voltam para a fila.`
        : falta?.destino === 'perda'
          ? `Ficha concluída: ${plural(quantidade, 'peça', 'peças')} (${faltou} de perda).`
          : `Ficha concluída: ${plural(quantidade, 'peça', 'peças')}`,
    )
  }

  return (
    <>
      <IndicadorOffline pendentes={pendentes.length} />
      {perguntarFimDoTurno && turno && (
        <AvisoFimDoTurno
          fimDoTurno={new Date(turno.fimDoTurno)}
          encerraEm={noFimDoTurno.encerraEm}
          temFichaRodando={fichaRodando}
          onContinuar={() => confirmarHoraExtra.mutate()}
          onPausarESair={pausarESairNoFimDoTurno}
          pendente={confirmarHoraExtra.isPending}
        />
      )}
      <AnimatePresence>
        {sobreposicao === 'resumo' && (
          <TelaResumoDoDia key="resumo" nome={nome} onFechar={onFecharSobreposicao} />
        )}
        {sobreposicao === 'fechamento' && (
          <TelaMeuFechamento key="fechamento" onFechar={onFecharSobreposicao} />
        )}
      </AnimatePresence>
      <section
        className={cn(
          'grid min-h-0 flex-1 gap-4 p-4 md:gap-5 md:p-5 baixa:gap-3 baixa:p-3',
          emPasso
            ? 'grid-cols-1 grid-rows-1'
            : 'portrait:grid-rows-[auto_minmax(0,1fr)] landscape:grid-cols-[minmax(0,1.5fr)_minmax(20rem,1fr)]',
        )}
      >
        <div className="flex min-h-0 flex-col card p-5 md:p-6 baixa:p-4">
          {emAndamento ? (
            <FichaEmAndamento
              ficha={emAndamento}
              modo={modo}
              onModo={mudarModo}
              motivos={motivos.data ?? []}
              motivosPerda={motivosPerda.data ?? []}
              registrar={registrar}
              onConcluir={(q, falta) => concluir(emAndamento, q, falta)}
              onPausarESair={(motivoId) => void pausarESair(motivoId)}
              postoId={postoId}
              operadorId={operadorId}
              ajudante={ajudante}
              onAjudanteConfirmado={() => void invalidarSessao()}
              onRemoverAjudante={() => removerAjudante.mutate()}
            />
          ) : emAtividade ? (
            <AtividadeEmAndamento
              nome={emAtividade.atividade.nome}
              iniciadaEm={emAtividade.iniciadaEm}
              onEncerrar={encerrarAtividade}
            />
          ) : fila.isPending ? (
            <EsqueletoLinhas linhas={4} rotulo="Carregando a ficha" />
          ) : escolhendoAtividade ? (
            <EscolherAtividade
              atividades={atividades.data ?? []}
              onEscolher={iniciarAtividade}
              onVoltar={() => setEscolhendoAtividade(false)}
            />
          ) : (
            <AreaLivre
              temFila={aguardando.length > 0}
              onOutraAtividade={
                atividades.data && atividades.data.length > 0
                  ? () => setEscolhendoAtividade(true)
                  : undefined
              }
            />
          )}
        </div>
        <aside
          aria-label="Fila e fichas feitas"
          className={cn('flex min-h-0 flex-col overflow-hidden card', emPasso && 'hidden')}
        >
          <div
            role="tablist"
            className="grid shrink-0 grid-cols-2 gap-1 border-b border-border p-2"
          >
            <Aba
              ativa={aba === 'fila'}
              onClick={() => setAba('fila')}
              contador={filaVista ? aguardando.length : null}
            >
              Fila
            </Aba>
            <Aba
              ativa={aba === 'feitas'}
              onClick={() => setAba('feitas')}
              contador={feitas.data ? feitas.data.length : null}
            >
              Feitas hoje
            </Aba>
          </div>
          <div role="tabpanel" className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">
            {aba === 'fila' ? (
              <ListaFila
                fila={fila}
                aguardando={aguardando}
                bloqueio={
                  emAndamento
                    ? 'Para começar outra, conclua a atual ou toque em "Trocar de ficha".'
                    : emAtividade
                      ? 'Encerre a atividade para puxar uma ficha.'
                      : null
                }
                onComecar={(f) =>
                  void registrar(f.id, f.etapa.tempoPresetSeg > 0 ? 'iniciar_preset' : 'iniciar')
                }
              />
            ) : (
              <ListaFeitas feitas={feitas} />
            )}
          </div>
        </aside>
      </section>
    </>
  )
}

function Aba({
  ativa,
  onClick,
  contador,
  children,
}: {
  ativa: boolean
  onClick: () => void
  contador: number | null
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={ativa}
      onClick={onClick}
      className={cn(
        'flex h-14 press items-center justify-center gap-2 rounded-xl px-3 text-lg font-semibold transition-colors',
        ativa ? 'bg-elevated text-fg' : 'text-fg-muted hover:bg-elevated/60 hover:text-fg',
      )}
    >
      <span className="truncate">{children}</span>
      {contador != null && (
        <span
          className={cn(
            'min-w-8 rounded-full px-2 py-0.5 text-base tabular',
            ativa ? 'bg-brand text-brand-fg' : 'bg-elevated text-fg-muted',
          )}
        >
          {contador}
        </span>
      )}
    </button>
  )
}

function ListaFila({
  fila,
  aguardando,
  bloqueio,
  onComecar,
}: {
  fila: ReturnType<typeof useFila>
  aguardando: FichaDaFila[]
  /** Por que a fila não pode ser puxada agora (ficha ou atividade em andamento). */
  bloqueio: string | null
  onComecar: (f: FichaDaFila) => void
}) {
  if (fila.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <EsqueletoCartoes quantidade={3} />
      </div>
    )
  }
  // Sem rede com a fila em cache: segue mostrando o cache (o aviso de offline já aparece).
  if (fila.isError && !fila.data) {
    return (
      <EstadoVazio
        Icone={CloudOff}
        tom="atencao"
        titulo="Não deu para carregar a fila"
        texto={mensagemDeErro(fila.error)}
        acao={
          <Button size="tap" variant="secondary" onClick={() => void fila.refetch()}>
            Tentar de novo
          </Button>
        }
      />
    )
  }
  if (aguardando.length === 0) {
    return (
      <EstadoVazio
        Icone={Inbox}
        titulo="Nada aguardando neste setor"
        texto="Quando a gestão lançar pedidos para cá, as fichas aparecem aqui."
        className="h-full"
      />
    )
  }
  return (
    <div className="flex flex-col gap-2">
      {bloqueio && <p className="px-1 pb-1 text-base text-fg-subtle">{bloqueio}</p>}
      {aguardando.map((f, i) => (
        <CartaoFila
          key={f.id}
          ficha={f}
          ordem={i + 1}
          desabilitado={!!bloqueio}
          onComecar={() => onComecar(f)}
        />
      ))}
    </div>
  )
}

function ListaFeitas({ feitas }: { feitas: ReturnType<typeof useFeitasHoje> }) {
  if (feitas.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <EsqueletoCartoes quantidade={2} />
      </div>
    )
  }
  if (feitas.isError && !feitas.data) {
    return (
      <EstadoVazio
        Icone={CloudOff}
        tom="atencao"
        titulo="Não deu para carregar as fichas feitas"
        texto={mensagemDeErro(feitas.error)}
        acao={
          <Button size="tap" variant="secondary" onClick={() => void feitas.refetch()}>
            Tentar de novo
          </Button>
        }
      />
    )
  }
  const lista = feitas.data ?? []
  if (lista.length === 0) {
    return (
      <EstadoVazio
        Icone={ListChecks}
        titulo="Nenhuma ficha concluída hoje"
        texto="Cada ficha que você concluir aparece aqui, a mais recente em cima."
        className="h-full"
      />
    )
  }
  const pecas = lista.reduce((s, f) => s + f.quantidade, 0)
  return (
    <div className="flex flex-col gap-2">
      <p className="px-1 pb-1 text-base text-fg-muted tabular">
        {plural(lista.length, 'ficha', 'fichas')} · {plural(pecas, 'peça', 'peças')} hoje
      </p>
      <ItensFeitos lista={lista} />
    </div>
  )
}

function ItensFeitos({ lista }: { lista: FichaFeita[] }) {
  return (
    <ul className="flex flex-col gap-2">
      <AnimatePresence initial={false}>
        {lista.map((f) => (
          <motion.li
            key={f.id}
            layout
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="flex items-center gap-3 rounded-2xl border border-border bg-elevated/40 px-4 py-3"
          >
            <span
              aria-hidden
              className="grid size-10 shrink-0 place-items-center rounded-full bg-verde/15 text-verde"
            >
              <Check className="size-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-lg font-semibold">{f.produto}</span>
              <span className="block truncate text-sm text-fg-muted tabular">
                {hora(f.concluidoEm)} · Pedido #{f.pedido} · ficha {f.ficha.numero}/{f.ficha.total}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className="block text-2xl leading-none font-bold tabular">{f.quantidade}</span>
              <span className="block text-xs text-fg-muted">peças</span>
            </span>
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  )
}

/**
 * Sem ficha em andamento: o que fazer agora. O andamento do dia fica na aba
 * "Feitas hoje" ao lado, com a ficha que acabou de concluir no topo.
 */
function AreaLivre({
  temFila,
  onOutraAtividade,
}: {
  temFila: boolean
  /** Abre a escolha de atividade sem ficha; ausente quando não há nenhuma cadastrada. */
  onOutraAtividade?: (() => void) | undefined
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 landscape:justify-center landscape:gap-8">
      <div className="flex items-center gap-4 landscape:flex-col landscape:text-center">
        <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-brand/15 text-brand landscape:size-16">
          <Play className="size-7 landscape:size-8" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-2xl font-bold">Nenhuma ficha em andamento</p>
          <p className="text-lg text-fg-muted">
            {temFila
              ? 'Toque em uma ficha da fila para começar.'
              : 'Quando entrar ficha para este setor, ela aparece na fila.'}
          </p>
        </div>
      </div>
      {onOutraAtividade && (
        <Button
          size="tap"
          variant="secondary"
          className="w-full landscape:mx-auto landscape:max-w-xl"
          onClick={onOutraAtividade}
        >
          <Brush className="size-6 text-brand" aria-hidden /> Atividade sem ficha
        </Button>
      )}
    </div>
  )
}

/** Cartão da fila: posição, produto, pedido/ficha, prazo em pílula e quantidade. */
function CartaoFila({
  ficha: f,
  ordem,
  desabilitado,
  onComecar,
}: {
  ficha: FichaDaFila
  ordem: number
  desabilitado: boolean
  onComecar: () => void
}) {
  const produto = f.ficha.itemPedido.produto.nome
  const pedido = f.ficha.itemPedido.pedido
  const prazo = pedido.prazo ? new Date(pedido.prazo) : null
  // Mesma regra da gestão (meia-noite UTC da coluna DATE): prazo de hoje não está vencido.
  const vencido = prazoVencido(pedido.prazo)
  const hoje = prazoDeHoje(pedido.prazo)
  const nomeFicha = f.ficha.saldoDe
    ? `saldo da ficha ${f.ficha.saldoDe.numero}/${f.ficha.total}`
    : `ficha ${f.ficha.numero}/${f.ficha.total}`
  const descricao = `Pedido ${pedido.numero}, ${nomeFicha}, ${f.ficha.quantidade} peças${
    prazo ? `, prazo ${formatarData(prazo)}${vencido ? ', vencido' : hoje ? ', hoje' : ''}` : ''
  }`
  return (
    <button
      type="button"
      disabled={desabilitado}
      onClick={onComecar}
      aria-label={`Começar ${produto}. ${descricao}`}
      className="flex min-h-22 w-full press items-center gap-3 rounded-2xl border border-border bg-elevated/40 px-3 py-3 text-left transition-colors hover:border-brand/60 active:border-brand disabled:opacity-50 md:gap-4 md:px-4"
    >
      <span
        aria-hidden
        className="grid size-11 shrink-0 place-items-center rounded-xl bg-elevated text-base font-bold text-fg-muted tabular"
      >
        {ordem}º
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-lg font-semibold">{produto}</span>
        <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-muted">
          <span className="whitespace-nowrap">
            Pedido #{pedido.numero} · {nomeFicha}
          </span>
          {prazo && (
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular',
                vencido
                  ? 'bg-vermelho/15 text-vermelho'
                  : hoje
                    ? 'bg-amarelo/15 text-amarelo'
                    : 'bg-elevated text-fg-muted',
              )}
            >
              {vencido ? `Venceu ${diaMes(prazo)}` : hoje ? 'Prazo hoje' : `Prazo ${diaMes(prazo)}`}
            </span>
          )}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-2xl leading-none font-bold tabular">{f.ficha.quantidade}</span>
        <span className="block text-xs text-fg-muted">peças</span>
      </span>
      <ChevronRight className="size-6 shrink-0 text-fg-subtle" aria-hidden />
    </button>
  )
}

/** Resumo do operador em tela cheia; a fila continua montada por baixo (outbox viva). */
function TelaResumoDoDia({ nome, onFechar }: { nome: string; onFechar: () => void }) {
  const trpc = useTRPC()
  const resumo = useQuery({
    ...trpc.apontamento.resumoDoDia.queryOptions(),
    refetchInterval: 60_000,
  })
  const feitas = useFeitasHoje()
  return (
    <Sobreposicao rotulo="Resumo do dia" titulo="Meu dia" onFechar={onFechar}>
      {resumo.data ? (
        <>
          <ResumoDoDia nome={nome} {...resumo.data} />
          <section aria-label="Fichas feitas hoje" className="flex w-full max-w-3xl flex-col gap-3">
            <h3 className="text-sm font-semibold tracking-widest text-fg-muted uppercase">
              Fichas feitas hoje
            </h3>
            <ListaFeitas feitas={feitas} />
          </section>
        </>
      ) : resumo.isError ? (
        <EstadoVazio
          Icone={CloudOff}
          tom="atencao"
          tamanho="lg"
          titulo="Não deu para carregar o resumo agora"
          texto={mensagemDeErro(resumo.error)}
          acao={
            <Button size="tap" variant="secondary" onClick={() => void resumo.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      ) : (
        <div className="grid w-full max-w-3xl grid-cols-2 gap-3 md:grid-cols-4">
          <EsqueletoCartoes quantidade={4} />
        </div>
      )}
    </Sobreposicao>
  )
}

/** Fechamentos mensais do operador, com ciência por PIN (RF-T12). */
function TelaMeuFechamento({ onFechar }: { onFechar: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const fechamentos = useQuery(trpc.avaliacao.fechamentos.meus.queryOptions())
  const [erro, setErro] = useState<string | null>(null)
  const ciencia = useMutation(
    trpc.avaliacao.fechamentos.darCiencia.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: trpc.avaliacao.fechamentos.meus.queryKey() }),
      onError: (e) => setErro(mensagemDeErro(e)),
    }),
  )
  return (
    <Sobreposicao rotulo="Meu fechamento" titulo="Meu fechamento" onFechar={onFechar}>
      {fechamentos.data ? (
        <MeuFechamento
          fechamentos={fechamentos.data}
          erro={erro}
          pendente={ciencia.isPending}
          onCiencia={(fechamentoId, pin) => {
            setErro(null)
            ciencia.mutate({ fechamentoId, pin })
          }}
        />
      ) : fechamentos.isError ? (
        <EstadoVazio
          Icone={CloudOff}
          tom="atencao"
          tamanho="lg"
          titulo="Não deu para carregar o fechamento agora"
          texto={mensagemDeErro(fechamentos.error)}
          acao={
            <Button size="tap" variant="secondary" onClick={() => void fechamentos.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      ) : (
        <EsqueletoLinhas linhas={6} className="w-full max-w-5xl" rotulo="Carregando o fechamento" />
      )}
    </Sobreposicao>
  )
}

/** Tela cheia por cima do posto: barra fixa com o Voltar e corpo que rola por dentro. */
function Sobreposicao({
  rotulo,
  titulo,
  onFechar,
  children,
}: {
  rotulo: string
  titulo: string
  onFechar: () => void
  children: ReactNode
}) {
  return (
    <motion.section
      role="dialog"
      aria-modal="true"
      aria-label={rotulo}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-40 flex flex-col bg-ink"
    >
      <div className="flex h-18 shrink-0 items-center gap-4 border-b border-border px-4 md:px-6">
        <Button size="tap" variant="secondary" className="shrink-0 px-5" onClick={onFechar}>
          <ArrowLeft className="size-6" aria-hidden /> Voltar para a fila
        </Button>
        <p className="truncate text-2xl font-bold">{titulo}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col items-center gap-6 p-4 md:p-6">
          {children}
        </div>
      </div>
    </motion.section>
  )
}

interface FichaEmAndamentoProps {
  ficha: FichaDaFila
  modo: ModoFicha
  onModo: (modo: ModoFicha) => void
  motivos: { id: string; nome: string; ajudaOutroPosto?: boolean; fimDoExpediente?: boolean }[]
  motivosPerda: { id: string; nome: string }[]
  registrar: (
    fichaEtapaId: string,
    tipo: TipoEvento,
    extra?: { motivoPausaId?: string; quantidade?: number },
  ) => Promise<void>
  onConcluir: (quantidade: number, falta?: FaltaInformada) => void
  /** Passo "Antes de sair": pausa com o motivo escolhido e encerra a sessão. */
  onPausarESair: (motivoPausaId: string) => void
  postoId: string
  operadorId: string
  ajudante: Ajudante | null
  onAjudanteConfirmado: () => void
  onRemoverAjudante: () => void
}

function FichaEmAndamento({
  ficha,
  modo,
  onModo,
  motivos,
  motivosPerda,
  registrar,
  onConcluir,
  onPausarESair,
  postoId,
  operadorId,
  ajudante,
  onAjudanteConfirmado,
  onRemoverAjudante,
}: FichaEmAndamentoProps) {
  const agora = useRelogio()
  const resumo = reduzirEventos(eventosDoBanco(ficha.eventos), agora)
  // Durante a ficha o que o operador precisa saber é se está dentro do tempo
  // previsto — eficiência de verdade só fecha com a quantidade, na conclusão.
  const progresso = progressoTempo(resumo.tempoExecucaoSeg, ficha.tempoPrevistoSeg)
  const corRitmo = progresso > 1 ? 'vermelho' : progresso > 0.85 ? 'amarelo' : 'verde'
  const restante = Math.max(0, ficha.ficha.quantidade - resumo.quantidadeApontada)
  const voltar = () => onModo('acoes')
  const produto = ficha.ficha.itemPedido.produto.nome
  const pedidoFicha = `Pedido #${ficha.ficha.itemPedido.pedido.numero} · ${
    ficha.ficha.saldoDe
      ? `saldo da ficha ${ficha.ficha.saldoDe.numero} de ${ficha.ficha.total}`
      : `ficha ${ficha.ficha.numero} de ${ficha.ficha.total}`
  }`

  const selo =
    resumo.estado === 'pausada' ? (
      <SemaforoBadge cor="amarelo" texto="Pausada" Icone={Pause} />
    ) : resumo.estado === 'em_preset' ? (
      <SemaforoBadge cor="neutro" texto="Preparando" />
    ) : (
      <SemaforoBadge cor={corRitmo} />
    )
  const decorrido = resumo.estado === 'em_preset' ? resumo.tempoPresetSeg : resumo.tempoExecucaoSeg

  if (modo !== 'acoes') {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4 baixa:gap-2">
        {/* Faixa da ficha: o operador não perde de vista em qual ficha está. */}
        <div className="flex shrink-0 items-center gap-3 rounded-2xl bg-elevated/60 px-4 py-2 baixa:hidden">
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-semibold">{produto}</p>
            <p className="truncate text-sm text-fg-muted">{pedidoFicha}</p>
          </div>
          <span
            className={cn(
              'shrink-0 text-2xl font-bold tabular',
              resumo.estado === 'pausada' && 'text-fg-muted',
            )}
          >
            {formatarCronometro(decorrido)}
          </span>
          <span className="shrink-0 max-sm:hidden">{selo}</span>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          {(modo === 'motivos' || modo === 'sair') && (
            <Passo key={modo}>
              <CabecalhoPasso
                titulo={
                  modo === 'sair'
                    ? 'Antes de sair: por que a ficha vai ficar pausada?'
                    : 'Por que vai pausar?'
                }
                onVoltar={voltar}
              />
              {motivos.length === 0 ? (
                <EstadoVazio
                  tamanho="lg"
                  titulo="Nenhum motivo de pausa cadastrado"
                  texto="A gestão cadastra os motivos em Motivos de pausa."
                />
              ) : (
                <div className="grid min-h-0 flex-1 auto-rows-[minmax(7rem,auto)] grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] content-start gap-3 overflow-y-auto overscroll-contain">
                  {(modo === 'sair'
                    ? [...motivos].sort(
                        (a, b) => Number(!!b.fimDoExpediente) - Number(!!a.fimDoExpediente),
                      )
                    : motivos.filter((m) => !m.fimDoExpediente)
                  ).map((m) => (
                    <Button
                      key={m.id}
                      size="tap"
                      variant="secondary"
                      className="h-auto px-4 py-3 text-2xl leading-tight whitespace-normal"
                      onClick={() => {
                        if (modo === 'sair') {
                          onPausarESair(m.id)
                          return
                        }
                        voltar()
                        void registrar(ficha.id, 'pausar', { motivoPausaId: m.id })
                        // A saída está feita; a chegada é o PIN no outro tablet (ADR 0015).
                        if (m.ajudaOutroPosto) {
                          toast.info(
                            'No outro posto, peça para tocarem em "Chamar ajudante" e digite o seu PIN.',
                          )
                        }
                      }}
                    >
                      {m.nome}
                    </Button>
                  ))}
                </div>
              )}
            </Passo>
          )}
          {modo === 'apontar' && (
            <Passo key="apontar">
              <TecladoNumerico
                rotulo="Quantas peças ficaram prontas?"
                dica={`Faltam ${restante} de ${ficha.ficha.quantidade}. A ficha continua aberta.`}
                maximo={restante}
                avisoMaximo={avisoAcimaDaFicha(ficha.ficha.quantidade, restante)}
                onConfirmar={(quantidade) => {
                  voltar()
                  void registrar(ficha.id, 'apontar', { quantidade })
                }}
                onCancelar={voltar}
              />
            </Passo>
          )}
          {modo === 'trocar' && (
            <Passo key="trocar">
              <TecladoNumerico
                rotulo="Quantas peças ficaram prontas?"
                dica={`As que faltarem voltam para a fila como saldo. Faltam ${restante} de ${ficha.ficha.quantidade}.`}
                minimo={0}
                maximo={restante}
                avisoMaximo={avisoAcimaDaFicha(ficha.ficha.quantidade, restante)}
                textoConfirmar="Trocar de ficha"
                onConfirmar={(quantidade) =>
                  onConcluir(quantidade, quantidade < restante ? { destino: 'saldo' } : undefined)
                }
                onCancelar={voltar}
              />
            </Passo>
          )}
          {modo === 'concluir' && (
            <Passo key="concluir">
              <ConfirmarConclusao
                quantidadeFicha={ficha.ficha.quantidade}
                quantidadeApontada={resumo.quantidadeApontada}
                motivosPerda={motivosPerda}
                onConfirmar={onConcluir}
                onCancelar={voltar}
              />
            </Passo>
          )}
          {modo === 'ajudante' && (
            <Passo key="ajudante">
              <EntrarAjudante
                postoId={postoId}
                operadorId={operadorId}
                onConfirmado={() => {
                  onAjudanteConfirmado()
                  voltar()
                }}
                onCancelar={voltar}
              />
            </Passo>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 baixa:gap-3">
      <div className="flex shrink-0 items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-base text-fg-muted">{pedidoFicha}</p>
          <h2 className="line-clamp-2 text-2xl font-bold text-balance lg:text-3xl baixa:line-clamp-1 baixa:text-xl">
            {produto}
          </h2>
          <p className="mt-1 text-xl text-fg-muted tabular baixa:mt-0 baixa:text-base">
            {resumo.quantidadeApontada > 0
              ? `${resumo.quantidadeApontada} de ${ficha.ficha.quantidade} peças apontadas`
              : `${ficha.ficha.quantidade} peças`}
          </p>
          {ajudante && (
            <p className="mt-2 inline-flex items-center gap-2 rounded-full bg-brand/15 py-1 pr-1 pl-3 text-base font-semibold text-brand">
              <UserPlus className="size-5" aria-hidden />
              Com ajuda de {primeiroNome(ajudante.nome)}
              <button
                type="button"
                className="grid size-12 place-items-center rounded-full hover:bg-brand/20 active:bg-brand/30"
                aria-label={`Remover ajudante ${primeiroNome(ajudante.nome)}`}
                onClick={onRemoverAjudante}
              >
                <X className="size-5" aria-hidden />
              </button>
            </p>
          )}
        </div>
        <span className="shrink-0">{selo}</span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-center">
        {resumo.estado === 'em_preset' ? (
          <Cronometro
            decorridoSeg={resumo.tempoPresetSeg}
            previstoSeg={ficha.etapa.tempoPresetSeg}
          />
        ) : (
          <Cronometro
            decorridoSeg={resumo.tempoExecucaoSeg}
            previstoSeg={ficha.tempoPrevistoSeg}
            pausado={resumo.estado === 'pausada'}
          />
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-3">
        <div
          className={cn(
            'grid gap-3',
            resumo.estado === 'em_preset' ? 'grid-cols-1' : 'grid-cols-2',
          )}
        >
          {resumo.estado === 'em_preset' && (
            <Button size="hero" variant="verde" onClick={() => void registrar(ficha.id, 'iniciar')}>
              <Play className="size-8" aria-hidden /> Começar produção
            </Button>
          )}
          {resumo.estado === 'em_execucao' && (
            <>
              <Button size="hero" variant="amarelo" onClick={() => onModo('motivos')}>
                <Pause className="size-8" aria-hidden /> Pausar
              </Button>
              <Button size="hero" variant="verde" onClick={() => onModo('concluir')}>
                <Square className="size-8" aria-hidden /> Concluir
              </Button>
            </>
          )}
          {resumo.estado === 'pausada' && (
            <>
              <Button
                size="hero"
                variant="verde"
                onClick={() => void registrar(ficha.id, 'retomar')}
              >
                <Play className="size-8" aria-hidden /> Retomar
              </Button>
              <Button size="hero" variant="secondary" onClick={() => onModo('concluir')}>
                <Square className="size-8" aria-hidden /> Concluir
              </Button>
            </>
          )}
        </div>
        {resumo.estado !== 'em_preset' && (
          <div className="grid grid-cols-3 gap-3">
            <Button
              size="tap"
              variant="secondary"
              className="h-auto min-h-tap px-3 text-lg leading-tight whitespace-normal"
              onClick={() => onModo('apontar')}
              disabled={resumo.estado !== 'em_execucao'}
            >
              <Hash className="size-6 shrink-0" aria-hidden /> Apontar quantidade
            </Button>
            <Button
              size="tap"
              variant="secondary"
              className="h-auto min-h-tap px-3 text-lg leading-tight whitespace-normal"
              onClick={() => onModo('ajudante')}
              disabled={!!ajudante}
            >
              <UserPlus className="size-6 shrink-0" aria-hidden /> Chamar ajudante
            </Button>
            {/* Pausar uma para começar outra: o que falta vira saldo na fila (ADR 0014). */}
            <Button
              size="tap"
              variant="secondary"
              className="h-auto min-h-tap px-3 text-lg leading-tight whitespace-normal"
              onClick={() => onModo('trocar')}
            >
              <Repeat className="size-6 shrink-0" aria-hidden /> Trocar de ficha
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

/** Título do passo com o Voltar no topo: sempre visível, deitado ou em pé. */
function CabecalhoPasso({ titulo, onVoltar }: { titulo: string; onVoltar: () => void }) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-3">
      <h3 className="text-2xl font-semibold text-balance">{titulo}</h3>
      <Button variant="secondary" size="tap" className="shrink-0 px-5" onClick={onVoltar}>
        <ArrowLeft className="size-6" aria-hidden /> Voltar
      </Button>
    </div>
  )
}

function Passo({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.15 }}
      className="flex min-h-0 flex-1 flex-col gap-4 baixa:gap-3"
    >
      {children}
    </motion.div>
  )
}

/** Ajudante escolhe o nome e digita o próprio PIN; a API confirma (RF-T07). */
function EntrarAjudante({
  postoId,
  operadorId,
  onConfirmado,
  onCancelar,
}: {
  postoId: string
  operadorId: string
  onConfirmado: () => void
  onCancelar: () => void
}) {
  const trpc = useTRPC()
  const operadores = useQuery(trpc.auth.operadoresDoPosto.queryOptions({ postoId }))
  const [erro, setErro] = useState<string | null>(null)
  const confirmar = useMutation(
    trpc.apontamento.confirmarAjudante.mutationOptions({
      onSuccess: onConfirmado,
      onError: (e) => setErro(mensagemDeErro(e)),
    }),
  )
  return (
    <>
      <CabecalhoPasso titulo="Quem vai ajudar nesta ficha?" onVoltar={onCancelar} />
      <EscolherOperador
        operadores={(operadores.data ?? []).filter((o) => o.id !== operadorId)}
        carregando={operadores.isPending}
        erro={erro}
        pendente={confirmar.isPending}
        onPin={(id, pin) => {
          setErro(null)
          confirmar.mutate({ operadorId: id, pin })
        }}
      />
    </>
  )
}
